import React, { FormEvent, useEffect, useCallback, useState } from 'react'
import * as yup from 'yup'
import useSWR, { SWRResponse } from 'swr'
import IconButton from '@mui/material/IconButton'
import InfoIcon from '@mui/icons-material/Info'
import {
  AppBar,
  Autocomplete,
  Box,
  Button,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  Toolbar,
} from '@mui/material'
import { IconCircleX } from '@tabler/icons-react'
import { useDropzone } from 'react-dropzone'
import { useFormik } from 'formik'
import { FormattedMessage, useIntl } from 'react-intl'
import SuccessModal from '../../../components/SuccessModal'
import AddNewItem from './AddNewItem'
import EditPhoneRow from './EditPhoneRow'
import EditLeadEmailRow from './EditLeadEmailRow'
import CreateContactRow from './CreateContactRow'
import EditEmailRow from './EditEmailRow'
import FormErrorsModal from '../../../components/FormErrorsModal'
import FileUploadArea from '../../../components/File/FileUpload'
import Loading from '../../../components/Loading'
import axiosInstance from '../../../utils/axiosInstance'
import { TresolvedAny } from '../../../types'
import { TcreatCustomerObject } from '../../../constants'
import CustomerStyleSection, { StyleData } from './CustomerStyleSection'
import { useDispatch, useSelector } from '../../../store/store'
import { setTriggerCustomersRefetch } from '../../../store/slices/general'
import { validateFileSize } from '../../../utils/fileValidation'

interface Iprops {
  handleClose: () => void
}

interface Iselect {
  label: string
  value: string
}

export const customerTypes: Iselect[] = [
  { label: 'Reseller', value: 'partner' },
  { label: 'Reseller customer', value: 'resellerCustomer' },
  { label: 'Direct', value: 'direct' },
  { label: 'White lable', value: 'whiteLable' },
]

export type CustomerTypeValues = (typeof customerTypes)[number]['value']

export const customerValidationSchema = yup.object({
  name: yup.string().required('Name is required').min(2, 'Too Short!').max(500, 'Too Long!'),
  url: yup
    .string()
    .nullable()
    .test('is-valid-url', 'Invalid URL (e.g. https://example.com)', (value) => {
      if (!value) return true
      try {
        const url = new URL(value.startsWith('http') ? value : `https://${value}`)
        return !!url.hostname
      } catch {
        return false
      }
    })
    .max(200, 'Too Long!'),
  newEmail: yup.string().email('Invalid email').nullable(),
  adAccountId: yup.string().nullable(),
  newPhone: yup
    .string()
    .nullable()
    .matches(/^\+?[\d\s\-()]{6,20}$/, {
      message: 'Invalid phone number',
      excludeEmptyString: true,
    }),
  address: yup.string().max(500, 'Too Long!'),
  type: yup
    .string()
    .required('Required')
    .oneOf(customerTypes.map((i) => i.value)),
  connectedReseller: yup.string().nullable(),
  connectedAppUser: yup.string().nullable(),
  logo: yup.object({ upload: yup.string() }).nullable(),
})

const CreateOrder: React.FC<Iprops> = ({ handleClose }) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const { role, customer: currentUserCustomer } = useSelector((state) => state.user.userInfo)
  const isReseller = role === 'externalReseller'

  const [customerType, setCustomerType] = useState<CustomerTypeValues>(
    isReseller ? 'resellerCustomer' : 'partner'
  )
  const [logoFile, setlogoFile] = useState<null | File>(null)

  const [formikErrors, setFormikErrors] = useState<string[]>([])
  const [errorsModalOpen, setErrorsModalOpen] = useState(false)
  const [successModalOpen, setSuccessModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const [addNewEmailClicked, setAddNewEmailClicked] = useState(false)
  const [addNewPhoneClicked, setAddNewPhoneClicked] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [newPhone, setNewPhone] = useState('')
  const [phones, setPhones] = useState<string[]>([])
  const [emails, setEmails] = useState<string[]>([])
  const [leadEmails, setLeadEmails] = useState<string[]>([])
  const [defaultInvoiceEmail] = useState('')
  const [defaultPreviewEmail, setDefaultPreviewEmail] = useState('')
  const [defaultEmail, setDefaultEmail] = useState('')

  // Style state management
  const [styleData, setStyleData] = useState<StyleData>({
    primaryColor: '',
    secondaryColor: '',
    accentColor: '',
    backgroundColor: '',
    textColor: '',
    fontColor: '',
  })

  const handleStyleChange = (field: keyof StyleData, value: string) => {
    setStyleData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleNewEmailApprove = useCallback(() => {
    setEmails([...emails, newEmail])
  }, [emails, newEmail])
  const handleNewPhoneApprove = useCallback(() => {
    setPhones([...phones, newPhone])
  }, [phones, newPhone])
  const [addNewLeadEmailClicked, setAddNewLeadEmailClicked] = useState(false)
  const [newLeadEmail, setNewLeadEmail] = useState('')
  const handleNewLeadEmailApprove = useCallback(() => {
    setLeadEmails([...leadEmails, newLeadEmail])
  }, [leadEmails, newLeadEmail])

  const { data: fetchedCustomers }: SWRResponse = useSWR('/customers', axiosInstance)
  const { data: fetchedAppUsers }: SWRResponse = useSWR(
    role === 'admin' ? '/app-users' : null,
    axiosInstance
  )
  const appUsers = fetchedAppUsers?.data?.appUsers

  const onDropLogoFile = useCallback((acceptedFiles: [null] | File[]) => {
    if (acceptedFiles?.length) {
      if (acceptedFiles[0]) {
        const sizeError = validateFileSize([acceptedFiles[0]])
        if (sizeError) {
          setFormikErrors([sizeError])
          setErrorsModalOpen(true)
          return
        }
      }
      setlogoFile(acceptedFiles[0])
    }
  }, [])

  const {
    acceptedFiles: acceptedFilesLogo,
    getRootProps: getRootPropsLogo,
    getInputProps: getInputPropsLogo,
    isDragActive: isDragActiveLogo,
  } = useDropzone({
    onDrop: onDropLogoFile,
  })

  const formik = useFormik({
    initialValues: {
      name: '',
      url: '',
      adAccountId: '',
      address: '',
      type: isReseller ? 'resellerCustomer' : customerTypes[0].value,
      connectedReseller: isReseller ? currentUserCustomer?.id || '' : '',
      appUser: [] as string[],
      logo: '',
    },
    validationSchema: customerValidationSchema,
    onSubmit: async (values) => {
      const newFormData: TcreatCustomerObject = {
        name: values.name,
        url: values.url,
        phoneNumbers: JSON.stringify(phones),
        emails: JSON.stringify(emails),
        leadEmails: JSON.stringify(leadEmails),
        adAccountId: values.adAccountId,
        defaultEmail,
        defaultInvoiceEmail,
        defaultPreviewEmail,
        address: values.address,
        type: values.type,
        connectedReseller: null,
        appUser: null,
        logo: null,
      }

      if (logoFile) {
        newFormData.logo = {
          upload: logoFile,
        }
      }
      if (values.connectedReseller) {
        newFormData.connectedReseller = { connect: { id: values.connectedReseller } }
      }
      if (values.appUser && values.appUser.length > 0) {
        newFormData.appUser = { connect: values.appUser.map((id) => ({ id })) }
      }

      // Only create style if user has set any colors
      const hasStyleData = Object.values(styleData).some((color) => color.trim() !== '')
      if (hasStyleData) {
        const styleCreateData: any = {}
        if (styleData.primaryColor.trim()) styleCreateData.primaryColor = styleData.primaryColor
        if (styleData.secondaryColor.trim())
          styleCreateData.secondaryColor = styleData.secondaryColor
        if (styleData.accentColor.trim()) styleCreateData.accentColor = styleData.accentColor
        if (styleData.backgroundColor.trim())
          styleCreateData.backgroundColor = styleData.backgroundColor
        if (styleData.textColor.trim()) styleCreateData.textColor = styleData.textColor
        if (styleData.fontColor.trim()) styleCreateData.fontColor = styleData.fontColor

        newFormData.style = {
          create: styleCreateData,
        }
      }

      const graphqlData = {
        variables: { data: newFormData },
      }

      const formData = new FormData()
      formData.append('graphqlData', JSON.stringify(graphqlData))
      if (logoFile) formData.append('logo', logoFile)

      try {
        const res = await axiosInstance.post('/createCustomer', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        if (res.data?.createCustomer?.name) {
          setSuccessModalOpen(true)
          dispatch(setTriggerCustomersRefetch(true))
        }
      } catch (error: TresolvedAny) {
        const err = error as TresolvedAny
        setFormikErrors([err.response.data.message])
        setErrorsModalOpen(true)
      }
      setIsLoading(false)
    },
  })

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()

    const errors = Object.values(formik.errors)
    if (errors.length) {
      setErrorsModalOpen(true)
      setFormikErrors(errors as string[])
      return
    }
    if (logoFile) await formik.setFieldValue('logo', { upload: logoFile })

    setIsLoading(true)
    formik.handleSubmit()
  }

  const { isUserRoleInternal } = useSelector((state) => state.user.userInfo)

  // Update formik values when current user's customer is loaded (for resellers)
  useEffect(() => {
    if (
      isReseller &&
      currentUserCustomer?.id &&
      formik.values.connectedReseller !== currentUserCustomer.id
    ) {
      formik.setFieldValue('connectedReseller', currentUserCustomer.id)
    }
  }, [isReseller, currentUserCustomer?.id, formik])

  return (
    <div className="CreateCustomer">
      <form onSubmit={(e) => handleSubmit(e)}>
        <AppBar sx={{ position: 'sticky' }} color="default">
          <Toolbar>
            <IconButton
              size="medium"
              edge="start"
              color="inherit"
              onClick={handleClose}
              aria-label="close"
            >
              <IconCircleX size="50px" color="gray" />
            </IconButton>
            <Typography sx={{ ml: 3, flex: 1, textAlign: 'center' }} variant="h3" component="div">
              <FormattedMessage id="create-new-customer" />
            </Typography>
            <Button variant="contained" color="secondary" autoFocus size="large" type="submit">
              <FormattedMessage id="create" />
            </Button>
          </Toolbar>
        </AppBar>
        {isLoading ? (
          <Loading />
        ) : (
          <div className="formContainer">
            <Container maxWidth="md" className="mainData subFormContainer">
              <div className="header">
                <FormattedMessage id="customer" />
              </div>
              <Grid container className="content">
                <TextField
                  fullWidth
                  id="name"
                  name="name"
                  placeholder="Customer name"
                  type="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
                <FormControl fullWidth>
                  <InputLabel id="type">
                    <FormattedMessage id="customer-type" />
                  </InputLabel>
                  <Select
                    fullWidth
                    labelId="type"
                    id="type"
                    name="type"
                    value={formik.values.type}
                    label="customerType"
                    disabled={isReseller}
                    onChange={(e) => {
                      formik.handleChange(e)
                      setCustomerType(e.target.value)
                    }}
                  >
                    {(isReseller
                      ? customerTypes.filter((i) => i.value === 'resellerCustomer')
                      : customerTypes
                    ).map((i: Iselect) => (
                      <MenuItem key={i.value} value={i.value}>
                        {i.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {customerType === 'resellerCustomer' && (
                  <FormControl fullWidth>
                    <InputLabel id="connectedReseller">
                      <FormattedMessage id="connected-reseller" />
                    </InputLabel>
                    <Select
                      fullWidth
                      labelId="connectedReseller"
                      id="connectedReseller"
                      name="connectedReseller"
                      value={formik.values.connectedReseller}
                      label={intl.formatMessage({ id: 'connected-reseller' })}
                      onChange={formik.handleChange}
                      disabled={isReseller}
                    >
                      {(isReseller
                        ? [currentUserCustomer].filter(Boolean)
                        : fetchedCustomers?.data?.customers || []
                      ).map((i: { name: string; id: string }) => (
                        <MenuItem key={i.id} value={i.id}>
                          {i.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {isUserRoleInternal && (
                  // <FormControl fullWidth>
                  //   <InputLabel id="connectedAppUser">
                  //     <FormattedMessage id="connected-app-user" />
                  //   </InputLabel>
                  //   <Select
                  //     fullWidth
                  //     labelId="appUser"
                  //     id="appUser"
                  //     name="appUser"
                  //     value={formik.values.appUser}
                  //     label={intl.formatMessage({ id: 'connected-app-user' })}
                  //     onChange={formik.handleChange}
                  //   >
                  //     {appUsers?.map((i: { firstName: string; email: string; id: string }) => (
                  //       <MenuItem key={i.id} value={i.id}>
                  //         {`${i.firstName} - ${i.email}`}
                  //       </MenuItem>
                  //     ))}
                  //   </Select>
                  // </FormControl>
                  //   <Autocomplete
                  //   fullWidth
                  //   options={campaigns}
                  //   getOptionLabel={(option: IdAndName) => option.name}
                  //   onChange={(e, value) => {
                  //     formik.setFieldValue('metaCampaign', value?.id)
                  //   }}
                  //   renderInput={(params) => (
                  //     <TextField
                  //       {...params}
                  //       label={<FormattedMessage id="metaCampaign" />}
                  //       id="metaCampaign"
                  //       name="metaCampaign"
                  //       placeholder="Meta Campaign"
                  //       type="text"
                  //       value={formik.values.metaCampaign}
                  //       onChange={formik.handleChange}
                  //       error={formik.touched.metaCampaign && Boolean(formik.errors.metaCampaign)}
                  //     />
                  //   )}
                  // />
                  <Autocomplete
                    multiple
                    fullWidth
                    options={appUsers || []}
                    getOptionLabel={(option: { firstName: string; email: string; id: string }) =>
                      `${option.firstName} ${option.firstName && '-'} ${option.email}`
                    }
                    onChange={(e, value) => {
                      formik.setFieldValue(
                        'appUser',
                        value.map((user) => user.id)
                      )
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={<FormattedMessage id="appUser" />}
                        id="appUser"
                        name="appUser"
                        placeholder="App Users"
                        error={formik.touched.appUser && Boolean(formik.errors.appUser)}
                      />
                    )}
                  />
                )}
              </Grid>
            </Container>
            <Container maxWidth="md" className="customerDetails subFormContainer">
              <div className="header">
                <FormattedMessage id="contact-details" />
              </div>
              <Grid container className="content">
                <TextField
                  fullWidth
                  id="url"
                  name="url"
                  placeholder="Website (e.g. https://example.com)"
                  type="text"
                  value={formik.values.url}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.url && Boolean(formik.errors.url)}
                  helperText={formik.touched.url && formik.errors.url}
                />

                <TextField
                  fullWidth
                  id="address"
                  name="address"
                  placeholder="Address"
                  type="text"
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.address && Boolean(formik.errors.address)}
                  helperText={formik.touched.address && formik.errors.address}
                />
              </Grid>

              <div className="header" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <FormattedMessage id="email" />
                  <Typography variant="caption" color="text.secondary">
                    <FormattedMessage id="new-lead-notifications" />
                  </Typography>
                </Box>
                <Tooltip
                  title={
                    formik.values.type === 'resellerCustomer'
                      ? "These emails are used for new lead notifications. Notifications will also be sent to the parent reseller's emails."
                      : 'These emails are used for new lead notifications.'
                  }
                  arrow
                  placement="right"
                >
                  <IconButton size="small" sx={{ padding: '4px' }}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
              <Grid container className="content">
                {emails.length ? (
                  emails.map((email) => (
                    <EditEmailRow
                      emails={emails}
                      email={email}
                      setEmails={setEmails}
                      key={email}
                      defaultPreviewEmail={defaultPreviewEmail}
                      setDefaultPreviewEmail={setDefaultPreviewEmail}
                      defaultEmail={defaultEmail}
                      setDefaultEmail={setDefaultEmail}
                    />
                  ))
                ) : (
                  <div />
                )}
                <div className="newContactCont">
                  {addNewEmailClicked && (
                    <div>
                      <div className="newEmail">
                        <CreateContactRow
                          type="email"
                          setContact={setNewEmail}
                          setItemClicked={() => setAddNewEmailClicked(false)}
                          handleApprove={() => handleNewEmailApprove()}
                        />
                      </div>
                    </div>
                  )}
                  <AddNewItem item="E-mail" handleAddItem={() => setAddNewEmailClicked(true)} />
                </div>
              </Grid>

              <div className="header" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <FormattedMessage id="lead-emails" />
                  <Typography variant="caption" color="text.secondary">
                    <FormattedMessage id="cv-upload-notifications" />
                  </Typography>
                </Box>
                <Tooltip
                  title="These emails will receive CV Upload Notifications when applicants submit their resumes."
                  arrow
                  placement="right"
                >
                  <IconButton size="small" sx={{ padding: '4px' }}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
              <Grid container className="content">
                {leadEmails.length ? (
                  leadEmails.map((email) => (
                    <EditLeadEmailRow
                      key={email}
                      email={email}
                      emails={leadEmails}
                      setEmails={setLeadEmails}
                    />
                  ))
                ) : (
                  <div />
                )}
                <div className="newContactCont">
                  {addNewLeadEmailClicked && (
                    <div>
                      <div className="newEmail">
                        <CreateContactRow
                          type="email"
                          setContact={setNewLeadEmail}
                          setItemClicked={() => setAddNewLeadEmailClicked(false)}
                          handleApprove={() => handleNewLeadEmailApprove()}
                        />
                      </div>
                    </div>
                  )}
                  <AddNewItem
                    item="Lead E-mail"
                    handleAddItem={() => setAddNewLeadEmailClicked(true)}
                  />
                </div>
              </Grid>
              <div className="header">
                <FormattedMessage id="phone" />
              </div>
              <Grid container className="content">
                {phones.length ? (
                  phones.map((phone) => (
                    <EditPhoneRow key={phone} phone={phone} setPhones={setPhones} />
                  ))
                ) : (
                  <div />
                )}
                <div className="newContactCont">
                  {addNewPhoneClicked && (
                    <div>
                      <div className="newEmail">
                        <CreateContactRow
                          type="phone"
                          setContact={setNewPhone}
                          setItemClicked={() => setAddNewPhoneClicked(false)}
                          handleApprove={() => handleNewPhoneApprove()}
                        />
                      </div>
                    </div>
                  )}
                  <AddNewItem
                    item={intl.formatMessage({ id: 'phone-number' })}
                    handleAddItem={() => setAddNewPhoneClicked(true)}
                  />
                </div>
              </Grid>
            </Container>
            <Container maxWidth="md" className="logo subFormContainer">
              <div className="header">Logo</div>
              <div className="content">
                <FileUploadArea
                  title="Logo"
                  getInputProps={getInputPropsLogo}
                  getRootProps={getRootPropsLogo}
                  acceptedFiles={acceptedFilesLogo}
                  isDragActive={isDragActiveLogo}
                  withResults
                />
              </div>
            </Container>

            {/* Customer Style Section */}
            <CustomerStyleSection styleData={styleData} onStyleChange={handleStyleChange} />
          </div>
        )}

        <FormErrorsModal
          errorsModalOpen={errorsModalOpen}
          formikErrors={formikErrors}
          setErrorsModalOpen={setErrorsModalOpen}
        />
        <SuccessModal
          successModalOpen={successModalOpen}
          handleClose={handleClose}
          setSuccessModalOpen={setSuccessModalOpen}
          message={intl.formatMessage({ id: 'created-successfully' })}
        />
      </form>
    </div>
  )
}

export default CreateOrder
